import { useState } from 'react';
import { Dialog } from '@workspace/design-system/src/components';
import { Channel } from '@src/models/channel';
import { ChannelInfoTab } from './channel-info-tab';
import { CustomerInfoTab } from './customer-info-tab';
import type { CustomerInfoFormData } from './customer-info-edit-modal';
import { ChannelPlanTab } from './channel-plan-tab';
import { ChannelPaymentTab } from './channel-payment-tab';
import { useUpsertChannelContact } from '@src/hooks/channel/mutations';
import { useToast } from '@workspace/design-system/src/components';

interface ChannelDetailModalProps {
  onClose: () => void;
  channel: Channel;
  onApplyPlan?: () => void;
  initialTab?: TabType;
}

type TabType = 'info' | 'customer' | 'plan' | 'payment';

const tabs: { value: TabType; label: string }[] = [
  { value: 'info', label: '채널 정보' },
  { value: 'plan', label: '요금제 내역' },
  { value: 'payment', label: '결제 내역' },
  { value: 'customer', label: '고객 정보' },
];

export function ChannelDetailModal({ onClose, channel, onApplyPlan, initialTab }: ChannelDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>(initialTab ?? 'info');
  const upsertContactMutation = useUpsertChannelContact();
  const { openToast } = useToast();

  const handleSaveCustomerInfo = async (data: CustomerInfoFormData) => {
    try {
      await upsertContactMutation.mutateAsync({
        channelId: channel.channelId,
        body: {
          customerName: data.customerName,
          businessType: data.businessType,
          contactName: data.contactName,
          contactTitle: data.contactTitle,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          memo: data.memo,
        },
      });
      openToast({ type: 'confirm', message: '고객 정보가 저장되었습니다.' });
    } catch (error) {
      openToast({ type: 'destructive', message: '고객 정보 저장에 실패했습니다.' });
    }
  };

  return (
    <Dialog>
      <Dialog.Content className="max-w-[1200px] h-[700px] flex flex-col">
        {/* 탭 + 우측 액션 */}
        <div className="flex items-center justify-between px-8 pt-8 border-b border-[#F0F0F0]">
          <div className="flex gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`pb-4 text-[16px] font-semibold transition-colors relative ${activeTab === tab.value
                  ? 'text-[#3C78F4]'
                  : 'text-[#BFBFBF] hover:text-[#595959]'
                  }`}
              >
                {tab.label}
                {activeTab === tab.value && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#3C78F4]" />
                )}
              </button>
            ))}
          </div>

        </div>

        {/* 탭 콘텐츠 */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {activeTab === 'info' && (
            <ChannelInfoTab
              key={`info-${channel.channelId}`}
              channel={channel}
            />
          )}



          {activeTab === 'plan' && (
            <ChannelPlanTab key={`plan-${channel.channelId}`} channel={channel} onApplyPlan={onApplyPlan} />
          )}

          {activeTab === 'payment' && (
            <ChannelPaymentTab key={`payment-${channel.channelId}`} channel={channel} />
          )}

          {activeTab === 'customer' && (
            <CustomerInfoTab
              key={`customer-${channel.channelId}`}
              channel={channel}
              onSaveCustomerInfo={handleSaveCustomerInfo}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F0F0F0] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 min-w-[99px] px-6 bg-[#F0F0F0] text-[#101010] rounded-xl text-[16px] font-bold leading-[20px] hover:bg-[#E6E6E6] transition-colors"
          >
            닫기
          </button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

